import { useEffect, useState, useMemo } from 'react';
import { IVehicleContextProps, VehicleContext } from '../context/VehicleContext';
import { useStateValue } from '../hooks/UseStateValue';
import { useConfigContext } from '../context/ConfigContext';
import { useUserContext } from '../context/UserContext';
import { ageInYears } from '../helpers/shared';
import { ProductNames } from '../interfaces/interfaces';
import { excludedManufacturers } from '../helpers/vehicle';

type Props = {
    children: React.ReactElement;
};

const VehicleProvider = ({ children }: Props) => {
    const [{ model, selection }] = useStateValue();
    const config = useConfigContext();
    const { age } = useUserContext();
    const [olderCar, setOlderCar] = useState<boolean>(false);
    const [carNotInList, setCarNotInList] = useState<boolean>(false);
    const [isLeasingCar, setIsLeasingCar] = useState<boolean | undefined>();
    const [trackingUnit, setTrackingUnit] = useState<boolean | undefined>();
    const [trackingUnitShown, setTrackingUnitShown] = useState<boolean | undefined>();
    const [tariffGroup, setTariffGroup] = useState<number>();
    const [vehicleName, setVehicleName] = useState<string>('');
    const [validLeasing, setValidLeasing] = useState<boolean>(true);
    const [presentLeasing, setPresentLeasing] = useState<boolean>(false);
    const [originalRegistrationDate, setOriginalRegistrationDate] = useState<string>();
    const [manufacturerExcluded, setManufacturerExcluded] = useState<boolean>(false);
    const [haveContentInsurance, setHaveContentInsurance] = useState<boolean | null>(false);
    const [carCanProceed, setCarCanProceed] = useState<boolean>(true);
    const [noContent3738, setNoContent3738] = useState(false);

    useEffect(() => {
        if (config.product === ProductNames.CAR) {
            const dmrData = model[ProductNames.CAR].dmr;
            if (dmrData) {
                setVehicleName(`${dmrData.manufacturerName} ${dmrData.modelName}`);
                setTariffGroup(dmrData.tariffGroup);
                setOlderCar(dmrData.olderCar);
                setIsLeasingCar(!!dmrData.registration?.leasing);
                if (selection.vehicleSelectionMode === 'MM') {
                    setIsLeasingCar(!!selection.leasingCar);
                }
                setTrackingUnit(dmrData.trackingUnit);
                setTrackingUnitShown(dmrData.trackingUnitShown);
                setOriginalRegistrationDate(dmrData.originalRegistrationDate);
                setManufacturerExcluded(excludedManufacturers.includes(dmrData.manufacturerName.toLowerCase()));
            }
            setCarNotInList(selection?.carNotInList ?? false);

            // To show RMO for tariff group 37/38 non-customers/no-content-insurence, we have to reset status values
            setCarCanProceed(true);
            setHaveContentInsurance(false);
            setNoContent3738(false);
            if (model[ProductNames.CAR].status) {
                setCarCanProceed(model[ProductNames.CAR].status?.canProceed ?? true);
                setHaveContentInsurance(model[ProductNames.CAR].status?.haveContentInsurance ?? false);
                setNoContent3738(!model[ProductNames.CAR].status?.haveContentInsurance && dmrData?.tariffGroup! > 36);
            }
        }
    }, [model, config.product, selection]);

    useEffect(() => {
        if (!!tariffGroup && !!age && selection.leasingCar) {
            setValidLeasing((tariffGroup > 35 && age > 29) || tariffGroup < 36); // Leasing: Hvis tarifgruppe over 35 skal man være over 29.
        }

        setPresentLeasing(false);
        if (isLeasingCar && originalRegistrationDate) {
            setPresentLeasing(ageInYears(originalRegistrationDate) < 3);
        }
    }, [isLeasingCar, tariffGroup, age, originalRegistrationDate, selection.leasingCar]);

    const items: IVehicleContextProps = useMemo(
        () => ({
            olderCar,
            carNotInList,
            tariffGroup,
            vehicleName,
            isLeasingCar,
            trackingUnit,
            trackingUnitShown,
            validLeasing,
            presentLeasing,
            carCanProceed,
            manufacturerExcluded,
            haveContentInsurance,
            noContent3738,
            setTrackingUnit,
            setTrackingUnitShown,
            setNoContent3738
        }),
        [
            olderCar,
            carNotInList,
            tariffGroup,
            vehicleName,
            isLeasingCar,
            trackingUnit,
            trackingUnitShown,
            validLeasing,
            presentLeasing,
            carCanProceed,
            manufacturerExcluded,
            haveContentInsurance,
            noContent3738,
            setTrackingUnit,
            setTrackingUnitShown,
            setNoContent3738
        ]
    );

    return <VehicleContext.Provider value={items}>{children}</VehicleContext.Provider>;
};

export default VehicleProvider;
